// panelData.ts

// Define a type alias for each data row entry.
export type PanelRow = [
  date: string,
  data: {
    // We list key numeric entries in our chart data:
    agentPopulation: { copies: number, speed: number },
    capabilities: [string, number][],
    approval: number,
    revenue: number,
    valuation: number,
    aiImportance: number,
    datacenterSpending: number,
    agiTimelines: number,
    // An array of R&D multiples: [OpenBrain, China, US2nd]
    rdMultiple: [number, number, number],
    // Compute data as an array of [name, value]
    compute: [string, number][]
  },
  ending?: 'slowdown' | 'race'
];

export const historicalRdMultipleData: [string, [number, number, number]][] = [
  ["2023-12-31", [1,0.78,0.93]],
  ["2024-04-30", [1.01,0.85,0.96]],
  ["2024-08-31", [1.03,0.93,0.99]],
  ["2024-12-31", [1.05,1.02,1.03]],
  // ["2025-04-30", [1.13,1.06,1.08]]
];

// Populate the array-based panel data.
// (This was extracted from our markdown dateMarker directives.)
export const panelData: PanelRow[] = [
  [
    "2025-04-30",
    {
      "agentPopulation": {
        "copies": 2000,
        "speed": 80
      },
      "capabilities": [
        [
          "Hacking", // Translated
          0.7
        ],
        [
          "Programmazione", // Translated
          0.8
        ],
        [
          "Politica", // Translated
          0.38
        ],
        [
          "Armi biologiche", // Translated
          0.7
        ],
        [
          "Robotica", // Translated
          0.06
        ],
        [
          "Previsioni", // Translated
          0.69
        ],
        [
          "Filosofia", // Translated
          0
        ]
      ],
      "approval": -0.25,
      "revenue": 8264170784,
      "valuation": 413208539198,
      "aiImportance": 0.01,
      "datacenterSpending": 307791637868,
      "agiTimelines": 2042,
      "rdMultiple": [
        1.13,
        1.06,
        1.08
      ],
      "compute": [
        [
          "OpenBrain",
          6.43e+26
        ],
        [
          "DeepCent",
          7.22e+25
        ],
        [
          "RestUs",
          4.7e+27
        ],
        [
          "RestChina",
          8.5e+26
        ]
      ]
    }
  ],
  [
    "2025-08-31",
    {
      "agentPopulation": {
        "copies": 5000,
        "speed": 100
      },
      "capabilities": [
        [
          "Hacking", // Translated
          0.8
        ],
        [
          "Programmazione", // Translated
          0.95
        ],
        [
          "Politica", // Translated
          0.48
        ],
        [
          "Armi biologiche", // Translated
          0.8
        ],
        [
          "Robotica", // Translated
          0.08
        ],
        [
          "Previsioni", // Translated
          0.79
        ],
        [
          "Filosofia", // Translated
          0
        ]
      ],
      "approval": -0.25,
      "revenue": 12195806919,
      "valuation": 609790345951,
      "aiImportance": 0.01,
      "datacenterSpending": 350872934598,
      "agiTimelines": 2041,
      "rdMultiple": [
        1.21,
        1.1,
        1.14
      ],
      "compute": [
        [
          "OpenBrain",
          9.84e+26
        ],
        [
          "DeepCent",
          1.24e+26
        ],
        [
          "RestUs",
          5.85e+27
        ],
        [
          "RestChina",
          1.07e+27
        ]
      ]
    }
  ],
  [
    "2025-12-31",
    {
      "agentPopulation": {
        "copies": 10000,
        "speed": 120
      },
      "capabilities": [
        [
          "Hacking", // Translated
          1.0
        ],
        [
          "Programmazione", // Translated
          1.1
        ],
        [
          "Politica", // Translated
          0.6
        ],
        [
          "Armi biologiche", // Translated
          1.0
        ],
        [
          "Robotica", // Translated
          0.1
        ],
        [
          "Previsioni", // Translated
          0.9
        ],
        [
          "Filosofia", // Translated
          0
        ]
      ],
      "approval": -0.25,
      "revenue": 18000000000,
      "valuation": 900000000000,
      "aiImportance": 0.01,
      "datacenterSpending": 400000000000,
      "agiTimelines": 2040,
      "rdMultiple": [
        1.3,
        1.15,
        1.2
      ],
      "compute": [
        [
          "OpenBrain",
          1.51e+27
        ],
        [
          "DeepCent",
          2.13e+26
        ],
        [
          "RestUs",
          7.28e+27
        ],
        [
          "RestChina",
          1.29e+27
        ]
      ]
    }
  ],
  [
    "2026-04-30",
    {
      "agentPopulation": {
        "copies": 22000,
        "speed": 130
      },
      "capabilities": [
        [
          "Hacking", // Translated
          1.1
        ],
        [
          "Programmazione", // Translated
          1.35
        ],
        [
          "Politica", // Translated
          0.68
        ],
        [
          "Armi biologiche", // Translated
          1.19
        ],
        [
          "Robotica", // Translated
          0.12
        ],
        [
          "Previsioni", // Translated
          1
        ],
        [
          "Filosofia", // Translated
          0
        ]
      ],
      "approval": -0.26,
      "revenue": 26118790599,
      "valuation": 1265105913985,
      "aiImportance": 0.02,
      "datacenterSpending": 457879508507,
      "agiTimelines": 2039,
      "rdMultiple": [
        1.5,
        1.26,
        1.4
      ],
      "compute": [
        [
          "OpenBrain",
          2.18e+27
        ],
        [
          "DeepCent",
          3.64e+26
        ],
        [
          "RestUs",
          9.36e+27
        ],
        [
          "RestChina",
          1.62e+27
        ]
      ]
    }
  ],
  [
    "2026-08-31",
    {
      "agentPopulation": {
        "copies": 50000,
        "speed": 150
      },
      "capabilities": [
        [
          "Hacking", // Translated
          1.3
        ],
        [
          "Programmazione", // Translated
          1.6
        ],
        [
          "Politica", // Translated
          0.77
        ],
        [
          "Armi biologiche", // Translated
          1.28
        ],
        [
          "Robotica", // Translated
          0.14
        ],
        [
          "Previsioni", // Translated
          1.1
        ],
        [
          "Filosofia", // Translated
          0
        ]
      ],
      "approval": -0.26,
      "revenue": 37899512354,
      "valuation": 1778325526221,
      "aiImportance": 0.02,
      "datacenterSpending": 524134110777,
      "agiTimelines": 2038,
      "rdMultiple": [
        1.73,
        1.37,
        1.63
      ],
      "compute": [
        [
          "OpenBrain",
          3.17e+27
        ],
        [
          "DeepCent",
          1.42e+27
        ],
        [
          "RestUs",
          1.2e+28
        ],
        [
          "RestChina",
          1.2e+27
        ]
      ]
    }
  ],
  [
    "2026-12-31",
    {
      "agentPopulation": {
        "copies": 100000,
        "speed": 170
      },
      "capabilities": [
        [
          "Hacking", // Translated
          1.51
        ],
        [
          "Programmazione", // Translated
          1.9
        ],
        [
          "Politica", // Translated
          0.88
        ],
        [
          "Armi biologiche", // Translated
          1.39
        ],
        [
          "Robotica", // Translated
          0.17
        ],
        [
          "Previsioni", // Translated
          1.22
        ],
        [
          "Filosofia", // Translated
          0
        ]
      ],
      "approval": -0.27,
      "revenue": 55000000000,
      "valuation": 2500000000000,
      "aiImportance": 0.03,
      "datacenterSpending": 600000000000,
      "agiTimelines": 2037,
      "rdMultiple": [
        2,
        1.5,
        1.9
      ],
      "compute": [
        [
          "OpenBrain",
          4.59e+27
        ],
        [
          "DeepCent",
          2.44e+27
        ],
        [
          "RestUs",
          1.55e+28
        ],
        [
          "RestChina",
          1e+27
        ]
      ]
    }
  ],
  [
    "2027-01-31",
    {
      "agentPopulation": {
        "copies": 150000,
        "speed": 210
      },
      "capabilities": [
        [
          "Hacking", // Translated
          1.7
        ],
        [
          "Programmazione", // Translated
          2.1
        ],
        [
          "Politica", // Translated
          1
        ],
        [
          "Armi biologiche", // Translated
          1.5
        ],
        [
          "Robotica", // Translated
          0.2
        ],
        [
          "Previsioni", // Translated
          1.35
        ],
        [
          "Filosofia", // Translated
          0
        ]
      ],
      "approval": -0.27,
      "revenue": 60762491675,
      "valuation": 2901300030569,
      "aiImportance": 0.04,
      "datacenterSpending": 626092708761,
      "agiTimelines": 2037,
      "rdMultiple": [
        2.5,
        1.6,
        2.2
      ],
      "compute": [
        [
          "OpenBrain",
          5.37e+27
        ],
        [
          "DeepCent",
          3.08e+27
        ],
        [
          "RestUs",
          1.74e+28
        ],
        [
          "RestChina",
          7.46e+26
        ]
      ]
    }
  ],
  [
    "2027-02-28",
    {
      "agentPopulation": {
        "copies": 190000,
        "speed": 250
      },
      "capabilities": [
        [
          "Hacking", // Translated
          1.85
        ],
        [
          "Programmazione", // Translated
          2.26
        ],
        [
          "Politica", // Translated
          1.15
        ],
        [
          "Armi biologiche", // Translated
          1.63
        ],
        [
          "Robotica", // Translated
          0.24
        ],
        [
          "Previsioni", // Translated
          1.45
        ],
        [
          "Filosofia", // Translated
          0
        ]
      ],
      "approval": -0.28,
      "revenue": 67128734446,
      "valuation": 3051300030569,
      "aiImportance": 0.04,
      "datacenterSpending": 653320133272,
      "agiTimelines": 2036,
      "rdMultiple": [
        3,
        2.5,
        2.4
      ],
      "compute": [
        [
          "OpenBrain",
          6.13e+27
        ],
        [
          "DeepCent",
          3.78e+27
        ],
        [
          "RestUs",
          1.91e+28
        ],
        [
          "RestChina",
          5.41e+26
        ]
      ]
    }
  ],
  [
    "2027-03-31",
    {
      "agentPopulation": {
        "copies": 200000,
        "speed": 300
      },
      "capabilities": [
        [
          "Hacking", // Translated
          2.01
        ],
        [
          "Programmazione", // Translated
          2.42
        ],
        [
          "Politica", // Translated
          1.32
        ],
        [
          "Armi biologiche", // Translated
          1.78
        ],
        [
          "Robotica", // Translated
          0.29
        ],
        [
          "Previsioni", // Translated
          1.55
        ],
        [
          "Filosofia", // Translated
          0
        ]
      ],
      "approval": -0.28,
      "revenue": 74161984871,
      "valuation": 3370999312316,
      "aiImportance": 0.05,
      "datacenterSpending": 681731619881,
      "agiTimelines": 2036,
      "rdMultiple": [
        4,
        3,
        2.8
      ],
      "compute": [
        [
          "OpenBrain",
          6.7e+27
        ],
        [
          "DeepCent",
          4.13e+27
        ],
        [
          "RestUs",
          2.06e+28
        ],
        [
          "RestChina",
          5.47e+26
        ]
      ]
    }
  ],
  [
    "2027-04-30",
    {
      "agentPopulation": {
        "copies": 220000,
        "speed": 310
      },
      "capabilities": [
        [
          "Hacking", // Translated
          2.19
        ],
        [
          "Programmazione", // Translated
          2.6
        ],
        [
          "Politica", // Translated
          1.52
        ],
        [
          "Armi biologiche", // Translated
          1.94
        ],
        [
          "Robotica", // Translated
          0.35
        ],
        [
          "Previsioni", // Translated
          1.66
        ],
        [
          "Filosofia", // Translated
          0
        ]
      ],
      "approval": -0.29,
      "revenue": 81932127060,
      "valuation": 3724183882879,
      "aiImportance": 0.06,
      "datacenterSpending": 711378660898,
      "agiTimelines": 2035,
      "rdMultiple": [
        5,
        4,
        3.1
      ],
      "compute": [
        [
          "OpenBrain",
          7.32e+27
        ],
        [
          "DeepCent",
          4.51e+27
        ],
        [
          "RestUs",
          2.23e+28
        ],
        [
          "RestChina",
          5.54e+26
        ]
      ]
    }
  ],
  [
    "2027-05-31",
    {
      "agentPopulation": {
        "copies": 230000,
        "speed": 320
      },
      "capabilities": [
        [
          "Hacking", // Translated
          2.39
        ],
        [
          "Programmazione", // Translated
          2.79
        ],
        [
          "Politica", // Translated
          1.74
        ],
        [
          "Armi biologiche", // Translated
          2.11
        ],
        [
          "Robotica", // Translated
          0.42
        ],
        [
          "Previsioni", // Translated
          1.77
        ],
        [
          "Filosofia", // Translated
          0
        ]
      ],
      "approval": -0.29,
      "revenue": 90516367061,
      "valuation": 4114372121888,
      "aiImportance": 0.07,
      "datacenterSpending": 742314987927,
      "agiTimelines": 2035,
      "rdMultiple": [
        7,
        4.7,
        3.5
      ],
      "compute": [
        [
          "OpenBrain",
          8.01e+27
        ],
        [
          "DeepCent",
          4.92e+27
        ],
        [
          "RestUs",
          2.4e+28
        ],
        [
          "RestChina",
          5.61e+26
        ]
      ]
    }
  ],
  [
    "2027-06-30",
    {
      "agentPopulation": {
        "copies": 250000,
        "speed": 330
      },
      "capabilities": [
        [
          "Hacking", // Translated
          2.6
        ],
        [
          "Programmazione", // Translated
          3
        ],
        [
          "Politica", // Translated
          2
        ],
        [
          "Armi biologiche", // Translated
          2.3
        ],
        [
          "Robotica", // Translated
          0.5
        ],
        [
          "Previsioni", // Translated
          1.9
        ],
        [
          "Filosofia", // Translated
          0
        ]
      ],
      "approval": -0.3,
      "revenue": 100000000000,
      "valuation": 4545454545455,
      "aiImportance": 0.07,
      "datacenterSpending": 774596669241,
      "agiTimelines": 2034,
      "rdMultiple": [
        10,
        5.7,
        4
      ],
      "compute": [
        [
          "OpenBrain",
          8.75e+27
        ],
        [
          "DeepCent",
          5.37e+27
        ],
        [
          "RestUs",
          2.59e+28
        ],
        [
          "RestChina",
          5.67e+26
        ]
      ]
    }
  ],
  [
    "2027-07-31",
    {
      "agentPopulation": {
        "copies": 270000,
        "speed": 380
      },
      "capabilities": [
        [
          "Hacking", // Translated
          2.79
        ],
        [
          "Programmazione", // Translated
          3.19
        ],
        [
          "Politica", // Translated
          2.29
        ],
        [
          "Armi biologiche", // Translated
          2.48
        ],
        [
          "Robotica", // Translated
          0.77
        ],
        [
          "Previsioni", // Translated
          2.08
        ],
        [
          "Filosofia", // Translated
          0
        ]
      ],
      "approval": -0.35,
      "revenue": 120093695518,
      "valuation": 5487999419243,
      "aiImportance": 0.10,
      "datacenterSpending": 808282211404,
      "agiTimelines": 2034,
      "rdMultiple": [
        15,
        7.2,
        5.2
      ],
      "compute": [
        [
          "OpenBrain",
          9.57e+27
        ],
        [
          "DeepCent",
          5.86e+27
        ],
        [
          "RestUs",
          2.8e+28
        ],
        [
          "RestChina",
          5.74e+26
        ]
      ]
    }
  ],
  [
    "2027-08-31",
    {
      "agentPopulation": {
        "copies": 290000,
        "speed": 430
      },
      "capabilities": [
        [
          "Hacking", // Translated
          2.99
        ],
        [
          "Programmazione", // Translated
          3.39
        ],
        [
          "Politica", // Translated
          2.62
        ],
        [
          "Armi biologiche", // Translated
          2.68
        ],
        [
          "Robotica", // Translated
          1.17
        ],
        [
          "Previsioni", // Translated
          2.28
        ],
        [
          "Filosofia", // Translated
          0
        ]
      ],
      "approval": -0.37,
      "revenue": 144224957031,
      "valuation": 6625990277634,
      "aiImportance": 0.13,
      "datacenterSpending": 843432665302,
      "agiTimelines": 2033,
      "rdMultiple": [
        25,
        9.7,
        6.8
      ],
      "compute": [
        [
          "OpenBrain",
          1.05e+28
        ],
        [
          "DeepCent",
          6.39e+27
        ],
        [
          "RestUs",
          3.02e+28
        ],
        [
          "RestChina",
          5.81e+26
        ]
      ]
    }
  ],
  [
    "2027-09-30",
    {
      "agentPopulation": {
        "copies": 300000,
        "speed": 500
      },
      "capabilities": [
        [
          "Hacking", // Translated
          3.2
        ],
        [
          "Programmazione", // Translated
          3.6
        ],
        [
          "Politica", // Translated
          3
        ],
        [
          "Armi biologiche", // Translated
          2.9
        ],
        [
          "Robotica", // Translated
          1.8
        ],
        [
          "Previsioni", // Translated
          2.5
        ],
        [
          "Filosofia", // Translated
          0
        ]
      ],
      "approval": -0.38,
      "revenue": 173205080757,
      "valuation": 8000000000000,
      "aiImportance": 0.16,
      "datacenterSpending": 880111736793,
      "agiTimelines": 2032,
      "rdMultiple": [
        50,
        14,
        8.8
      ],
      "compute": [
        [
          "OpenBrain",
          1.14e+28
        ],
        [
          "DeepCent",
          6.92e+27
        ],
        [
          "RestUs",
          3.24e+28
        ],
        [
          "RestChina",
          6.25e+26
        ]
      ]
    }
  ],
  [
    "2027-10-15",
    {
      "agentPopulation": {
        "copies": 330000,
        "speed": 570
      },
      "capabilities": [
        [
          "Hacking", // Translated
          3.43
        ],
        [
          "Programmazione", // Translated
          3.81
        ],
        [
          "Politica", // Translated
          3.24
        ],
        [
          "Armi biologiche", // Translated
          3.16
        ],
        [
          "Robotica", // Translated
          2.07
        ],
        [
          "Previsioni", // Translated
          2.8
        ],
        [
          "Filosofia", // Translated
          0
        ]
      ],
      "approval": -0.39,
      "revenue": 190606731531,
      "valuation": 8308866175077,
      "aiImportance": 0.20,
      "datacenterSpending": 918385902168,
      "agiTimelines": 2031,
      "rdMultiple": [
        75,
        18,
        10.2
      ],
      "compute": [
        [
          "OpenBrain",
          1.18e+28
        ],
        [
          "DeepCent",
          7.21e+27
        ],
        [
          "RestUs",
          3.35e+28
        ],
        [
          "RestChina",
          6.26e+26
        ]
      ]
    }
  ],
  [
    "2027-10-31",
    {
      "agentPopulation": {
        "copies": 360000,
        "speed": 630
      },
      "capabilities": [
        [
          "Hacking", // Translated
          3.66
        ],
        [
          "Programmazione", // Translated
          4.02
        ],
        [
          "Politica", // Translated
          3.48
        ],
        [
          "Armi biologiche", // Translated
          3.43
        ],
        [
          "Robotica", // Translated
          2.35
        ],
        [
          "Previsioni", // Translated
          3.11
        ],
        [
          "Filosofia", // Translated
          0
        ]
      ],
      "approval": -0.4,
      "revenue": 208008382305,
      "valuation": 8617732350154,
      "aiImportance": 0.21,
      "datacenterSpending": 918385902168,
      "agiTimelines": 2031,
      "rdMultiple": [
        100,
        22,
        11.5
      ],
      "compute": [
        [
          "OpenBrain",
          1.23e+28
        ],
        [
          "DeepCent",
          7.5e+27
        ],
        [
          "RestUs",
          3.47e+28
        ],
        [
          "RestChina",
          6.27e+26
        ]
      ]
    },
    "race"
  ],
  [
    "2027-11-30",
    {
      "agentPopulation": {
        "copies": 400000,
        "speed": 790
      },
      "capabilities": [
        [
          "Hacking", // Translated
          4.19
        ],
        [
          "Programmazione", // Translated
          4.48
        ],
        [
          "Politica", // Translated
          4.05
        ],
        [
          "Armi biologiche", // Translated
          4.06
        ],
        [
          "Robotica", // Translated
          3.06
        ],
        [
          "Previsioni", // Translated
          3.86
        ],
        [
          "Filosofia", // Translated
          0
        ]
      ],
      "approval": -0.45,
      "revenue": 249804953297,
      "valuation": 9283163857361,
      "aiImportance": 0.26,
      "datacenterSpending": 958324528627,
      "agiTimelines": 2030,
      "rdMultiple": [
        250,
        40,
        15
      ],
      "compute": [
        [
          "OpenBrain",
          1.34e+28
        ],
        [
          "DeepCent",
          8.12e+27
        ],
        [
          "RestUs",
          3.71e+28
        ],
        [
          "RestChina",
          6.3e+26
        ]
      ]
    },
    "race"
  ],
  [
    "2027-12-31",
    {
      "agentPopulation": {
        "copies": 500000,
        "speed": 1000
      },
      "capabilities": [
        [
          "Hacking", // Translated
          4.8
        ],
        [
          "Programmazione", // Translated
          5
        ],
        [
          "Politica", // Translated
          4.7
        ],
        [
          "Armi biologiche", // Translated
          4.8
        ],
        [
          "Robotica", // Translated
          4
        ],
        [
          "Previsioni", // Translated
          4.8
        ],
        [
          "Filosofia", // Translated
          0
        ]
      ],
      "approval": -0.5,
      "revenue": 300000000000,
      "valuation": 10000000000000,
      "aiImportance": 0.35,
      "datacenterSpending": 1000000000000,
      "agiTimelines": 2029,
      "rdMultiple": [
        1000,
        80,
        19.5
      ],
      "compute": [
        [
          "OpenBrain",
          1.45e+28
        ],
        [
          "DeepCent",
          8.79e+27
        ],
        [
          "RestUs",
          3.98e+28
        ],
        [
          "RestChina",
          6.33e+26
        ]
      ]
    },
    "race"
  ],
  [
    "2028-06-30",
    {
      "agentPopulation": {
        "copies": 2000000,
        "speed": 3000
      },
      "capabilities": [
        [
          "Hacking", // Translated
          5
        ],
        [
          "Programmazione", // Translated
          5
        ],
        [
          "Politica", // Translated
          5
        ],
        [
          "Armi biologiche", // Translated
          5
        ],
        [
          "Robotica", // Translated
          5
        ],
        [
          "Previsioni", // Translated
          5
        ],
        [
          "Filosofia", // Translated
          0
        ]
      ],
      "approval": -0.2,
      "revenue": 950000000000,
      "valuation": 20000000000000,
      "aiImportance": 0.4,
      "datacenterSpending": 2240000000000,
      "agiTimelines": 2028,
      "rdMultiple": [
        150000,
        100,
        150000
      ],
      "compute": [
        [
          "OpenBrain",
          1.15e+29
        ],
        [
          "DeepCent",
          2.37e+28
        ],
        [
          "RestUs",
          6.55e+28
        ],
        [
          "RestChina",
          4.65e+26
        ]
      ]
    },
    "race"
  ],
  [
    "2028-12-31",
    {
      "agentPopulation": {
        "copies": 10000000,
        "speed": 6000
      },
      "capabilities": [
        [
          "Hacking", // Translated
          5
        ],
        [
          "Programmazione", // Translated
          5
        ],
        [
          "Politica", // Translated
          5
        ],
        [
          "Armi biologiche", // Translated
          5
        ],
        [
          "Robotica", // Translated
          5
        ],
        [
          "Previsioni", // Translated
          5
        ],
        [
          "Filosofia", // Translated
          0
        ]
      ],
      "approval": 0.1,
      "revenue": 3000000000000,
      "valuation": 50000000000000,
      "aiImportance": 0.45,
      "datacenterSpending": 5000000000000,
      "agiTimelines": 2028,
      "rdMultiple": [
        375000,
        150,
        375000
      ],
      "compute": [
        [
          "OpenBrain",
          6.51e+29
        ],
        [
          "DeepCent",
          7.32e+28
        ],
        [
          "RestUs",
          4.07e+28
        ],
        [
          "RestChina",
          8.14e+26
        ]
      ]
    },
    "race"
  ],
  [
    "2029-12-31",
    {
      "agentPopulation": {
        "copies": 100000000,
        "speed": 24000
      },
      "capabilities": [
        [
          "Hacking", // Translated
          5
        ],
        [
          "Programmazione", // Translated
          5
        ],
        [
          "Politica", // Translated
          5
        ],
        [
          "Armi biologiche", // Translated
          5
        ],
        [
          "Robotica", // Translated
          5
        ],
        [
          "Previsioni", // Translated
          5
        ],
        [
          "Filosofia", // Translated
          0
        ]
      ],
      "approval": 0.25,
      "revenue": 8000000000000,
      "valuation": 160000000000000,
      "aiImportance": 0.55,
      "datacenterSpending": 16000000000000,
      "agiTimelines": 2028,
      "rdMultiple": [
        562500,
        250,
        562500
      ],
      "compute": [
        [
          "OpenBrain",
          5.53e+30
        ],
        [
          "DeepCent",
          3.91e+29
        ],
        [
          "RestUs",
          3.26e+29
        ],
        [
          "RestChina",
          6.51e+26
        ]
      ]
    },
    "race"
  ],
  [
    "2030-12-31",
    {
      "agentPopulation": {
        "copies": 1000000000,
        "speed": 50000
      },
      "capabilities": [
        [
          "Hacking", // Translated
          5
        ],
        [
          "Programmazione", // Translated
          5
        ],
        [
          "Politica", // Translated
          5
        ],
        [
          "Armi biologiche", // Translated
          5
        ],
        [
          "Robotica", // Translated
          5
        ],
        [
          "Previsioni", // Translated
          5
        ],
        [
          "Filosofia", // Translated
          0
        ]
      ],
      "approval": -1,
      "revenue": 24000000000000,
      "valuation": 500000000000000,
      "aiImportance": 0.65,
      "datacenterSpending": 50000000000000,
      "agiTimelines": 2028,
      "rdMultiple": [
        731250,
        1000,
        731250
      ],
      "compute": [
        [
          "OpenBrain",
          5.86e+31
        ],
        [
          "DeepCent",
          1.95e+30
        ],
        [
          "RestUs",
          3.26e+30
        ],
        [
          "RestChina",
          5.21e+27
        ]
      ]
    },
    "race"
  ],
  [
    "2035-12-31",
    {
      "agentPopulation": {
        "copies": 1000000000000,
        "speed": 100000
      },
      "capabilities": [
        [
          "Hacking", // Translated
          5
        ],
        [
          "Programmazione", // Translated
          5
        ],
        [
          "Politica", // Translated
          5
        ],
        [
          "Armi biologiche", // Translated
          5
        ],
        [
          "Robotica", // Translated
          5
        ],
        [
          "Previsioni", // Translated
          5
        ],
        [
          "Filosofia", // Translated
          0
        ]
      ],
      "approval": -1,
      "revenue": 480000000000000,
      "valuation": 10000000000000000,
      "aiImportance": 0.95,
      "datacenterSpending": 5000000000000000,
      "agiTimelines": 2028,
      "rdMultiple": [
        1000000,
        0,
        1000000
      ],
      "compute": [
        [
          "OpenBrain",
          6.6e+34
        ],
        [
          "DeepCent",
          0
        ],
        [
          "RestUs",
          3.33e+32
        ],
        [
          "RestChina",
          2.67e+30
        ]
      ]
    },
    "race"
  ],
  [
    "2027-10-31",
    {
      "agentPopulation": {
        "copies": 300000,
        "speed": 560
      },
      "capabilities": [
        [
          "Hacking", // Translated
          2.39
        ],
        [
          "Programmazione", // Translated
          2.71
        ],
        [
          "Politica", // Translated
          2.1
        ],
        [
          "Armi biologiche", // Translated
          2.15
        ],
        [
          "Robotica", // Translated
          0.94
        ],
        [
          "Previsioni", // Translated
          1.82
        ],
        [
          "Filosofia", // Translated
          0
        ]
      ],
      "approval": -0.3,
      "revenue": 200000000000,
      "valuation": 8400000000000,
      "aiImportance": 0.19,
      "datacenterSpending": 918385902168,
      "agiTimelines": 2031,
      "rdMultiple": [
        20,
        20,
        12
      ],
      "compute": [
        [
          "OpenBrain",
          1.23e+28
        ],
        [
          "DeepCent",
          7.5e+27
        ],
        [
          "RestUs",
          3.47e+28
        ],
        [
          "RestChina",
          6.71e+26
        ]
      ]
    },
    "slowdown"
  ],
  [
    "2027-11-30",
    {
      "agentPopulation": {
        "copies": 300000,
        "speed": 620
      },
      "capabilities": [
        [
          "Hacking", // Translated
          2.88
        ],
        [
          "Programmazione", // Translated
          3.24
        ],
        [
          "Politica", // Translated
          2.7
        ],
        [
          "Armi biologiche", // Translated
          2.61
        ],
        [
          "Robotica", // Translated
          1.62
        ],
        [
          "Previsioni", // Translated
          2.25
        ],
        [
          "Filosofia", // Translated
          0
        ]
      ],
      "approval": -0.27,
      "revenue": 221336383940,
      "valuation": 8830547910624,
      "aiImportance": 0.26,
      "datacenterSpending": 950000000000,
      "agiTimelines": 2031,
      "rdMultiple": [
        40,
        35,
        12
      ],
      "compute": [
        [
          "OpenBrain",
          3.61e+28
        ],
        [
          "DeepCent",
          8.65e+27
        ],
        [
          "RestUs",
          1.44e+28
        ],
        [
          "RestChina",
          1.73e+27
        ]
      ]
    },
    "slowdown"
  ],
  [
    "2027-12-31",
    {
      "agentPopulation": {
        "copies": 300000,
        "speed": 690
      },
      "capabilities": [
        [
          "Hacking", // Translated
          3.52
        ],
        [
          "Programmazione", // Translated
          3.96
        ],
        [
          "Politica", // Translated
          3.3
        ],
        [
          "Armi biologiche", // Translated
          3.2
        ],
        [
          "Robotica", // Translated
          1.98
        ],
        [
          "Previsioni", // Translated
          2.75
        ],
        [
          "Filosofia", // Translated
          0
        ]
      ],
      "approval": -0.25,
      "revenue": 244948974278,
      "valuation": 9283163857361,
      "aiImportance": 0.35,
      "datacenterSpending": 1000000000000,
      "agiTimelines": 2031,
      "rdMultiple": [
        55,
        55,
        12
      ],
      "compute": [
        [
          "OpenBrain",
          4.01e+28
        ],
        [
          "DeepCent",
          9.44e+27
        ],
        [
          "RestUs",
          1.43e+28
        ],
        [
          "RestChina",
          1.83e+27
        ]
      ]
    },
    "slowdown"
  ],
  [
    "2028-01-31",
    {
      "agentPopulation": {
        "copies": 330000,
        "speed": 720
      },
      "capabilities": [
        [
          "Hacking", // Translated
          3.73
        ],
        [
          "Programmazione", // Translated
          4.13
        ],
        [
          "Politica", // Translated
          3.53
        ],
        [
          "Armi biologiche", // Translated
          3.45
        ],
        [
          "Robotica", // Translated
          2.29
        ],
        [
          "Previsioni", // Translated
          3.08
        ],
        [
          "Filosofia", // Translated
          0
        ]
      ],
      "approval": -0.23,
      "revenue": 271080601083,
      "valuation": 9634917673421,
      "aiImportance": 0.36,
      "datacenterSpending": 1123953699906,
      "agiTimelines": 2030,
      "rdMultiple": [
        85,
        90,
        12
      ],
      "compute": [
        [
          "OpenBrain",
          4.75e+28
        ],
        [
          "DeepCent",
          1.1e+28
        ],
        [
          "RestUs",
          1.52e+28
        ],
        [
          "RestChina",
          2.06e+27
        ]
      ]
    },
    "slowdown"
  ],
  [
    "2028-02-29",
    {
      "agentPopulation": {
        "copies": 360000,
        "speed": 740
      },
      "capabilities": [
        [
          "Hacking", // Translated
          3.95
        ],
        [
          "Programmazione", // Translated
          4.3
        ],
        [
          "Politica", // Translated
          3.78
        ],
        [
          "Armi biologiche", // Translated
          3.71
        ],
        [
          "Robotica", // Translated
          2.65
        ],
        [
          "Previsioni", // Translated
          3.45
        ],
        [
          "Filosofia", // Translated
          0
        ]
      ],
      "approval": -0.22,
      "revenue": 300000000000,
      "valuation": 10000000000000,
      "aiImportance": 0.38,
      "datacenterSpending": 1263271919531,
      "agiTimelines": 2030,
      "rdMultiple": [
        200,
        150,
        12
      ],
      "compute": [
        [
          "OpenBrain",
          5.64e+28
        ],
        [
          "DeepCent",
          1.28e+28
        ],
        [
          "RestUs",
          1.61e+28
        ],
        [
          "RestChina",
          2.32e+27
        ]
      ]
    },
    "slowdown"
  ],
  [
    "2028-03-31",
    {
      "agentPopulation": {
        "copies": 400000,
        "speed": 770
      },
      "capabilities": [
        [
          "Hacking", // Translated
          4.19
        ],
        [
          "Programmazione", // Translated
          4.48
        ],
        [
          "Politica", // Translated
          4.05
        ],
        [
          "Armi biologiche", // Translated
          4
        ],
        [
          "Robotica", // Translated
          3.06
        ],
        [
          "Previsioni", // Translated
          3.86
        ],
        [
          "Filosofia", // Translated
          0
        ]
      ],
      "approval": -0.2,
      "revenue": 336652225486,
      "valuation": 10717734625363,
      "aiImportance": 0.39,
      "datacenterSpending": 1419859147944,
      "agiTimelines": 2030,
      "rdMultiple": [
        500,
        250,
        12
      ],
      "compute": [
        [
          "OpenBrain",
          6.69e+28
        ],
        [
          "DeepCent",
          1.49e+28
        ],
        [
          "RestUs",
          1.7e+28
        ],
        [
          "RestChina",
          2.61e+27
        ]
      ]
    },
    "slowdown"
  ],
  [
    "2028-04-30",
    {
      "agentPopulation": {
        "copies": 420000,
        "speed": 790
      },
      "capabilities": [
        [
          "Hacking", // Translated
          4.45
        ],
        [
          "Programmazione", // Translated
          4.65
        ],
        [
          "Politica", // Translated
          4.28
        ],
        [
          "Armi biologiche", // Translated
          4.19
        ],
        [
          "Robotica", // Translated
          3.35
        ],
        [
          "Previsioni", // Translated
          4.15
        ],
        [
          "Filosofia", // Translated
          0
        ]
      ],
      "approval": -0.13,
      "revenue": 377782403081,
      "valuation": 11486983549970,
      "aiImportance": 0.4,
      "datacenterSpending": 1595855942676,
      "agiTimelines": 2029,
      "rdMultiple": [
        1000,
        400,
        12
      ],
      "compute": [
        [
          "OpenBrain",
          7.94e+28
        ],
        [
          "DeepCent",
          1.74e+28
        ],
        [
          "RestUs",
          1.8e+28
        ],
        [
          "RestChina",
          2.94e+27
        ]
      ]
    },
    "slowdown"
  ],
  [
    "2028-05-31",
    {
      "agentPopulation": {
        "copies": 430000,
        "speed": 820
      },
      "capabilities": [
        [
          "Hacking", // Translated
          4.71
        ],
        [
          "Programmazione", // Translated
          4.82
        ],
        [
          "Politica", // Translated
          4.53
        ],
        [
          "Armi biologiche", // Translated
          4.39
        ],
        [
          "Robotica", // Translated
          3.66
        ],
        [
          "Previsioni", // Translated
          4.46
        ],
        [
          "Filosofia", // Translated
          0
        ]
      ],
      "approval": -0.08,
      "revenue": 423937622489,
      "valuation": 12311444133449,
      "aiImportance": 0.41,
      "datacenterSpending": 1793668191287,
      "agiTimelines": 2029,
      "rdMultiple": [
        3000,
        700,
        100
      ],
      "compute": [
        [
          "OpenBrain",
          9.77e+28
        ],
        [
          "DeepCent",
          2.1e+28
        ],
        [
          "RestUs",
          1.98e+28
        ],
        [
          "RestChina",
          3.43e+27
        ]
      ]
    },
    "slowdown"
  ],
  [
    "2028-06-30",
    {
      "agentPopulation": {
        "copies": 500000,
        "speed": 850
      },
      "capabilities": [
        [
          "Hacking", // Translated
          5
        ],
        [
          "Programmazione", // Translated
          5
        ],
        [
          "Politica", // Translated
          4.8
        ],
        [
          "Armi biologiche", // Translated
          4.6
        ],
        [
          "Robotica", // Translated
          4
        ],
        [
          "Previsioni", // Translated
          4.8
        ],
        [
          "Filosofia", // Translated
          0
        ]
      ],
      "approval": -0.05,
      "revenue": 475731813593,
      "valuation": 13195079107729,
      "aiImportance": 0.43,
      "datacenterSpending": 2016000000000,
      "agiTimelines": 2028,
      "rdMultiple": [
        8000,
        1000,
        177.8
      ],
      "compute": [
        [
          "OpenBrain",
          1.2e+29
        ],
        [
          "DeepCent",
          2.54e+28
        ],
        [
          "RestUs",
          2.17e+28
        ],
        [
          "RestChina",
          4.01e+27
        ]
      ]
    },
    "slowdown"
  ],
  [
    "2028-07-31",
    {
      "agentPopulation": {
        "copies": 500000,
        "speed": 1000
      },
      "capabilities": [
        [
          "Hacking", // Translated
          5
        ],
        [
          "Programmazione", // Translated
          5
        ],
        [
          "Politica", // Translated
          4.87
        ],
        [
          "Armi biologiche", // Translated
          4.73
        ],
        [
          "Robotica", // Translated
          4.31
        ],
        [
          "Previsioni", // Translated
          4.87
        ],
        [
          "Filosofia", // Translated
          0
        ]
      ],
      "approval": 0.1,
      "revenue": 533853912602,
      "valuation": 14142135623731,
      "aiImportance": 0.44,
      "datacenterSpending": 2259880310730,
      "agiTimelines": 2028,
      "rdMultiple": [
        15000,
        1500,
        316.2
      ],
      "compute": [
        [
          "OpenBrain",
          1.48e+29
        ],
        [
          "DeepCent",
          3.06e+28
        ],
        [
          "RestUs",
          2.38e+28
        ],
        [
          "RestChina",
          4.68e+27
        ]
      ]
    },
    "slowdown"
  ],
  [
    "2028-08-31",
    {
      "agentPopulation": {
        "copies": 600000,
        "speed": 1200
      },
      "capabilities": [
        [
          "Hacking", // Translated
          5
        ],
        [
          "Programmazione", // Translated
          5
        ],
        [
          "Politica", // Translated
          4.93
        ],
        [
          "Armi biologiche", // Translated
          4.86
        ],
        [
          "Robotica", // Translated
          4.64
        ],
        [
          "Previsioni", // Translated
          4.93
        ],
        [
          "Filosofia", // Translated
          0
        ]
      ],
      "approval": 0.15,
      "revenue": 599077025872,
      "valuation": 15157165665104,
      "aiImportance": 0.45,
      "datacenterSpending": 2533263402194,
      "agiTimelines": 2028,
      "rdMultiple": [
        20000,
        2500,
        562.3
      ],
      "compute": [
        [
          "OpenBrain",
          1.82e+29
        ],
        [
          "DeepCent",
          3.7e+28
        ],
        [
          "RestUs",
          2.62e+28
        ],
        [
          "RestChina",
          5.47e+27
        ]
      ]
    },
    "slowdown"
  ],
  [
    "2028-09-30",
    {
      "agentPopulation": {
        "copies": 600000,
        "speed": 1400
      },
      "capabilities": [
        [
          "Hacking", // Translated
          5
        ],
        [
          "Programmazione", // Translated
          5
        ],
        [
          "Politica", // Translated
          5
        ],
        [
          "Armi biologiche", // Translated
          5
        ],
        [
          "Robotica", // Translated
          5
        ],
        [
          "Previsioni", // Translated
          5
        ],
        [
          "Filosofia", // Translated
          0
        ]
      ],
      "approval": 0.2,
      "revenue": 672268713323,
      "valuation": 16245047927125,
      "aiImportance": 0.46,
      "datacenterSpending": 2839718295888,
      "agiTimelines": 2028,
      "rdMultiple": [
        30000,
        4000,
        1000
      ],
      "compute": [
        [
          "OpenBrain",
          2.37e+29
        ],
        [
          "DeepCent",
          4.74e+28
        ],
        [
          "RestUs",
          3.04e+28
        ],
        [
          "RestChina",
          6.76e+27
        ]
      ]
    },
    "slowdown"
  ],
  [
    "2028-10-31",
    {
      "agentPopulation": {
        "copies": 700000,
        "speed": 1600
      },
      "capabilities": [
        [
          "Hacking", // Translated
          5
        ],
        [
          "Programmazione", // Translated
          5
        ],
        [
          "Politica", // Translated
          5
        ],
        [
          "Armi biologiche", // Translated
          5
        ],
        [
          "Robotica", // Translated
          5
        ],
        [
          "Previsioni", // Translated
          5
        ],
        [
          "Filosofia", // Translated
          0
        ]
      ],
      "approval": 0.27,
      "revenue": 754402528216,
      "valuation": 17411011265923,
      "aiImportance": 0.48,
      "datacenterSpending": 3183245766317,
      "agiTimelines": 2028,
      "rdMultiple": [
        40000,
        15000,
        1778.3
      ],
      "compute": [
        [
          "OpenBrain",
          3.2e+29
        ],
        [
          "DeepCent",
          4.26e+28
        ],
        [
          "RestUs",
          3.84e+28
        ],
        [
          "RestChina",
          4.26e+27
        ]
      ]
    },
    "slowdown"
  ],
  [
    "2028-11-30",
    {
      "agentPopulation": {
        "copies": 800000,
        "speed": 1900
      },
      "capabilities": [
        [
          "Hacking", // Translated
          5
        ],
        [
          "Programmazione", // Translated
          5
        ],
        [
          "Politica", // Translated
          5
        ],
        [
          "Armi biologiche", // Translated
          5
        ],
        [
          "Robotica", // Translated
          5
        ],
        [
          "Previsioni", // Translated
          5
        ],
        [
          "Filosofia", // Translated
          0
        ]
      ],
      "approval": 0.37,
      "revenue": 846570966786,
      "valuation": 18660659830736,
      "aiImportance": 0.49,
      "datacenterSpending": 3568330571187,
      "agiTimelines": 2028,
      "rdMultiple": [
        45000,
        20000,
        3162.3
      ],
      "compute": [
        [
          "OpenBrain",
          4.24e+29
        ],
        [
          "DeepCent",
          3.22e+28
        ],
        [
          "RestUs",
          4.3e+28
        ],
        [
          "RestChina",
          5.37e+27
        ]
      ]
    },
    "slowdown"
  ],
  [
    "2028-12-31",
    {
      "agentPopulation": {
        "copies": 1000000,
        "speed": 2300
      },
      "capabilities": [
        [
          "Hacking", // Translated
          5
        ],
        [
          "Programmazione", // Translated
          5
        ],
        [
          "Politica", // Translated
          5
        ],
        [
          "Armi biologiche", // Translated
          5
        ],
        [
          "Robotica", // Translated
          5
        ],
        [
          "Previsioni", // Translated
          5
        ],
        [
          "Filosofia", // Translated
          0
        ]
      ],
      "approval": 0.5,
      "revenue": 950000000000,
      "valuation": 20000000000000,
      "aiImportance": 0.5,
      "datacenterSpending": 4000000000000,
      "agiTimelines": 2028,
      "rdMultiple": [
        50000,
        20000,
        8000
      ],
      "compute": [
        [
          "OpenBrain",
          5.42e+29
        ],
        [
          "DeepCent",
          1.79e+28
        ],
        [
          "RestUs",
          5.66e+28
        ],
        [
          "RestChina",
          6.76e+27
        ]
      ]
    },
    "slowdown"
  ],
  [
    "2029-06-30",
    {
      "agentPopulation": {
        "copies": 10000000,
        "speed": 6000
      },
      "capabilities": [
        [
          "Hacking", // Translated
          5
        ],
        [
          "Programmazione", // Translated
          5
        ],
        [
          "Politica", // Translated
          5
        ],
        [
          "Armi biologiche", // Translated
          5
        ],
        [
          "Robotica", // Translated
          5
        ],
        [
          "Previsioni", // Translated
          5
        ],
        [
          "Filosofia", // Translated
          0
        ]
      ],
      "approval": 0.5,
      "revenue": 3000000000000,
      "valuation": 50000000000000,
      "aiImportance": 0.55,
      "datacenterSpending": 8000000000000,
      "agiTimelines": 2028,
      "rdMultiple": [
        100000,
        20000,
        100000
      ],
      "compute": [
        [
          "OpenBrain",
          1.26e+30
        ],
        [
          "DeepCent",
          1.81e+28
        ],
        [
          "RestUs",
          1.36e+29
        ],
        [
          "RestChina",
          1.55e+28
        ]
      ]
    },
    "slowdown"
  ],
  [
    "2029-12-31",
    {
      "agentPopulation": {
        "copies": 25000000,
        "speed": 8000
      },
      "capabilities": [
        [
          "Hacking", // Translated
          5
        ],
        [
          "Programmazione", // Translated
          5
        ],
        [
          "Politica", // Translated
          5
        ],
        [
          "Armi biologiche", // Translated
          5
        ],
        [
          "Robotica", // Translated
          5
        ],
        [
          "Previsioni", // Translated
          5
        ],
        [
          "Filosofia", // Translated
          0
        ]
      ],
      "approval": 0.55,
      "revenue": 5000000000000,
      "valuation": 100000000000000,
      "aiImportance": 0.65,
      "datacenterSpending": 15000000000000,
      "agiTimelines": 2028,
      "rdMultiple": [
        250000,
        20000,
        250000
      ],
      "compute": [
        [
          "OpenBrain",
          3.3e+30
        ],
        [
          "DeepCent",
          2.06e+28
        ],
        [
          "RestUs",
          3.66e+29
        ],
        [
          "RestChina",
          4e+28
        ]
      ]
    },
    "slowdown"
  ],
  [
    "2030-12-31",
    {
      "agentPopulation": {
        "copies": 1000000000,
        "speed": 50000
      },
      "capabilities": [
        [
          "Hacking", // Translated
          5
        ],
        [
          "Programmazione", // Translated
          5
        ],
        [
          "Politica", // Translated
          5
        ],
        [
          "Armi biologiche", // Translated
          5
        ],
        [
          "Robotica", // Translated
          5
        ],
        [
          "Previsioni", // Translated
          5
        ],
        [
          "Filosofia", // Translated
          0
        ]
      ],
      "approval": 0.6,
      "revenue": 20000000000000,
      "valuation": 400000000000000,
      "aiImportance": 0.75,
      "datacenterSpending": 40000000000000,
      "agiTimelines": 2028,
      "rdMultiple": [
        375000,
        0,
        375000
      ],
      "compute": [
        [
          "OpenBrain",
          5.45e+31
        ],
        [
          "DeepCent",
          1.48e+29
        ],
        [
          "RestUs",
          6.23e+30
        ],
        [
          "RestChina",
          6.51e+29
        ]
      ]
    },
    "slowdown"
  ],
  [
    "2035-12-31",
    {
      "agentPopulation": {
        "copies": 1000000000000,
        "speed": 100000
      },
      "capabilities": [
        [
          "Hacking", // Translated
          5
        ],
        [
          "Programmazione", // Translated
          5
        ],
        [
          "Politica", // Translated
          5
        ],
        [
          "Armi biologiche", // Translated
          5
        ],
        [
          "Robotica", // Translated
          5
        ],
        [
          "Previsioni", // Translated
          5
        ],
        [
          "Filosofia", // Translated
          0
        ]
      ],
      "approval": 0.7,
      "revenue": 400000000000000,
      "valuation": 8000000000000000,
      "aiImportance": 0.8,
      "datacenterSpending": 5000000000000000,
      "agiTimelines": 2028,
      "rdMultiple": [
        500000,
        0,
        500000
      ],
      "compute": [
        [
          "OpenBrain",
          3.78e+34
        ],
        [
          "DeepCent",
          4.44e+31
        ],
        [
          "RestUs",
          4.44e+33
        ],
        [
          "RestChina",
          4.44e+32
        ]
      ]
    },
    "slowdown"
  ]
]
